import { useState, useEffect, useCallback } from 'react';
import { api, getErrorMessage } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { 
  LogIn, User, Users, Plus, Trash2, Camera, Hotel, Phone, CreditCard, 
  Calendar, Loader2, AlertTriangle, Bed, Info
} from 'lucide-react';
import { toast } from 'sonner';
import IdDocumentUpload from '@/components/IdDocumentUpload';
import PhoneInput from '@/components/PhoneInput';
import AddressInput from '@/components/AddressInput';

export default function CheckInDialog({ open, onOpenChange, bookingId, onCheckInComplete }) {
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [details, setDetails] = useState(null);
  const [idTypes, setIdTypes] = useState([]);
  const [searchingPhone, setSearchingPhone] = useState(false);
  const [availableRooms, setAvailableRooms] = useState([]);
  const [selectedRoomId, setSelectedRoomId] = useState('');
  const [loadingRooms, setLoadingRooms] = useState(false);
  
  // Form state similar to booking form
  const [formData, setFormData] = useState({
    check_in: '',
    check_out: '',
    notes: '',
    room_id: '', // For room assignment at check-in
    advance_paid: '',
    discount: '',
    primary_guest: {
      phone: '',
      first_name: '',
      last_name: '',
      email: '',
      id_type: '',
      id_number: '',
      id_proof_image: '',
      address: {
        address_line1: '',
        address_line2: '',
        city: '',
        state: '',
        pin_code: '',
        country: 'India'
      }
    },
    existing_guest_id: null,
    additional_guests: []
  });

  useEffect(() => {
    if (open && bookingId) {
      loadCheckInDetails();
    }
  }, [open, bookingId]);

  const loadCheckInDetails = async () => {
    setLoading(true);
    try {
      const [detailsRes, idTypesRes] = await Promise.all([
        api.get(`/bookings/${bookingId}/checkin-details`),
        api.get('/id-types')
      ]);
      
      const data = detailsRes.data;
      setDetails(data);
      setIdTypes(idTypesRes.data || []);
      
      // If booking doesn't have room assigned, load available rooms
      if (!data.booking?.room_id) {
        loadAvailableRooms();
      }
      
      // Parse dates for form
      const checkIn = data.booking?.check_in || data.booking?.check_in_date;
      const checkOut = data.booking?.check_out || data.booking?.check_out_date;
      
      let checkInFormatted = '';
      let checkOutFormatted = '';
      
      try {
        if (checkIn) {
          const d = new Date(checkIn);
          if (!isNaN(d.getTime())) checkInFormatted = d.toISOString().slice(0, 16);
        }
        if (checkOut) {
          const d = new Date(checkOut);
          if (!isNaN(d.getTime())) checkOutFormatted = d.toISOString().slice(0, 16);
        }
      } catch (e) {
        console.error('Error parsing dates:', e);
      }
      
      // Get primary guest info
      const pg = data.primary_guest;
      const nameParts = (pg?.name || '').split(' ');
      
      // Get additional guests info
      const additionalGuestIds = data.booking?.additional_guest_ids || [];
      const additionalGuestsData = additionalGuestIds.map(gid => {
        const ag = data.all_guests?.find(g => g.id === gid);
        const agNameParts = (ag?.name || '').split(' ');
        return {
          id: gid,
          first_name: ag?.first_name || agNameParts[0] || '',
          last_name: ag?.last_name || agNameParts.slice(1).join(' ') || '',
          phone: ag?.phone || '',
          id_type: ag?.id_type_name || '',
          id_number: ag?.id_number || '',
          id_proof_image: ag?.id_proof_image || ''
        };
      });
      
      setFormData({
        check_in: checkInFormatted,
        check_out: checkOutFormatted,
        notes: data.booking?.notes || '',
        advance_paid: data.booking?.advance_paid?.toString() || '0',
        discount: data.booking?.discount?.toString() || '0',
        primary_guest: {
          phone: pg?.phone || '',
          first_name: pg?.first_name || nameParts[0] || '',
          last_name: pg?.last_name || nameParts.slice(1).join(' ') || '',
          email: pg?.email || '',
          id_type: pg?.id_type_name || '',
          id_number: pg?.id_number || '',
          id_proof_image: pg?.id_proof_image || '',
          address: pg?.address && typeof pg.address === 'object' 
            ? pg.address 
            : { address_line1: '', address_line2: '', city: '', state: '', pin_code: '', country: 'India' }
        },
        existing_guest_id: pg?.id || null,
        additional_guests: additionalGuestsData
      });
    } catch (error) {
      toast.error('Failed to load check-in details');
      onOpenChange(false);
    } finally {
      setLoading(false);
    }
  };

  // Load available rooms for booking without room assignment
  const loadAvailableRooms = async () => {
    setLoadingRooms(true);
    try {
      const res = await api.get(`/rooms/available-for-checkin/${bookingId}`);
      const { matching_rooms, all_available_rooms, preferences } = res.data;
      
      // Prefer matching rooms, but show all as fallback
      setAvailableRooms(matching_rooms.length > 0 ? matching_rooms : all_available_rooms);
      
      // Auto-select first room if only one match
      if (matching_rooms.length === 1) {
        setSelectedRoomId(matching_rooms[0].id);
        setFormData(prev => ({ ...prev, room_id: matching_rooms[0].id }));
      }
    } catch (error) {
      console.error('Failed to load available rooms:', error);
    } finally {
      setLoadingRooms(false);
    }
  };

  // Search guest by phone number
  const searchGuestByPhone = useCallback(async (phone) => {
    if (!phone || phone.length < 10) return;
    
    setSearchingPhone(true);
    try {
      const res = await api.get(`/guests/search/phone/${encodeURIComponent(phone)}`);
      if (res.data.found && res.data.guest) {
        const guest = res.data.guest;
        const nameParts = (guest.name || '').split(' ');
        
        // Ensure address is an object
        const guestAddress = guest.address && typeof guest.address === 'object' 
          ? guest.address 
          : { address_line1: '', address_line2: '', city: '', state: '', pin_code: '', country: 'India' };
        
        setFormData(prev => ({
          ...prev,
          existing_guest_id: guest.id,
          primary_guest: {
            ...prev.primary_guest,
            phone: guest.phone || phone,
            first_name: guest.first_name || nameParts[0] || '',
            last_name: guest.last_name || nameParts.slice(1).join(' ') || '',
            email: guest.email || '',
            id_type: guest.id_type_name || '',
            id_number: guest.id_number || '',
            id_proof_image: guest.id_proof_image || '',
            address: guestAddress
          }
        }));
        toast.success('Guest found! Details auto-filled.');
      }
    } catch (error) {
      // Not found is OK
    } finally {
      setSearchingPhone(false);
    }
  }, []);

  const updatePrimaryGuest = (field, value) => {
    setFormData(prev => ({
      ...prev,
      primary_guest: { ...prev.primary_guest, [field]: value },
      existing_guest_id: field === 'phone' ? null : prev.existing_guest_id
    }));
  };

  const addAdditionalGuest = () => {
    const roomCapacity = details?.room_capacity || 2;
    if (1 + formData.additional_guests.length >= roomCapacity) {
      toast.error(`Maximum ${roomCapacity} guests allowed for this room`);
      return;
    }
    
    setFormData(prev => ({
      ...prev,
      additional_guests: [...prev.additional_guests, {
        first_name: '',
        last_name: '',
        phone: '',
        id_type: '',
        id_number: '',
        id_proof_image: ''
      }]
    }));
  };

  const removeAdditionalGuest = (index) => {
    setFormData(prev => ({
      ...prev,
      additional_guests: prev.additional_guests.filter((_, i) => i !== index)
    }));
  };

  const updateAdditionalGuest = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      additional_guests: prev.additional_guests.map((ag, i) => 
        i === index ? { ...ag, [field]: value } : ag
      )
    }));
  };

  const getTotalGuests = () => 1 + formData.additional_guests.length;
  const canCheckIn = () => getTotalGuests() <= (details?.room_capacity || 2);

  const handleCheckIn = async () => {
    // Validation - Room must be selected if not pre-assigned
    if (!details?.booking?.room_id && !formData.room_id) {
      toast.error('Please select a room for check-in');
      return;
    }
    
    // Validation - Primary guest ID is mandatory at check-in
    if (!formData.primary_guest.phone) {
      toast.error('Primary guest phone number is required');
      return;
    }
    if (!formData.primary_guest.first_name) {
      toast.error('Primary guest first name is required');
      return;
    }
    if (!formData.primary_guest.id_type || !formData.primary_guest.id_number) {
      toast.error('Primary guest ID document is mandatory for check-in');
      return;
    }
    
    // Validate address is provided - handle both string and object address formats
    const address = formData.primary_guest.address;
    const hasValidAddress = address && (
      typeof address === 'string' 
        ? address.trim() !== '' 
        : (address.address_line1 && address.address_line1.trim() !== '')
    );
    
    if (!hasValidAddress) {
      toast.error('Primary guest address is mandatory for check-in');
      return;
    }

    // Validate dates - cannot check-in with past dates
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    if (formData.check_in) {
      const checkInDate = new Date(formData.check_in);
      checkInDate.setHours(0, 0, 0, 0);
      if (checkInDate < today) {
        toast.error('Check-in date cannot be in the past');
        return;
      }
    }
    
    if (formData.check_out) {
      const checkOutDate = new Date(formData.check_out);
      const checkInDate = formData.check_in ? new Date(formData.check_in) : today;
      if (checkOutDate <= checkInDate) {
        toast.error('Check-out date must be after check-in date');
        return;
      }
    }

    if (!canCheckIn()) {
      toast.error(`Cannot check in ${getTotalGuests()} guests. Room capacity is ${details?.room_capacity}.`);
      return;
    }

    // Validate additional guests - First Name, Last Name, ID Type, ID Number, ID Image required. Mobile is NOT required.
    for (let i = 0; i < formData.additional_guests.length; i++) {
      const ag = formData.additional_guests[i];
      // Skip if guest entry is completely empty
      if (!ag.first_name?.trim() && !ag.last_name?.trim()) continue;
      
      // If guest has any data entered, validate all required fields
      if (!ag.first_name?.trim()) {
        toast.error(`Please enter first name for additional guest ${i + 1}`);
        return;
      }
      if (!ag.last_name?.trim()) {
        toast.error(`Please enter last name for additional guest ${i + 1}`);
        return;
      }
      if (!ag.id_type?.trim()) {
        toast.error(`Please select ID type for additional guest ${i + 1}`);
        return;
      }
      if (!ag.id_number?.trim()) {
        toast.error(`Please enter ID number for additional guest ${i + 1}`);
        return;
      }
      if (!ag.id_proof_image) {
        toast.error(`Please upload ID proof image for additional guest ${i + 1}`);
        return;
      }
    }

    setProcessing(true);
    try {
      const checkinData = {
        check_in: formData.check_in ? new Date(formData.check_in).toISOString() : null,
        check_out: formData.check_out ? new Date(formData.check_out).toISOString() : null,
        notes: formData.notes,
        advance_paid: parseFloat(formData.advance_paid || 0),
        discount: parseFloat(formData.discount || 0),
        primary_guest: formData.primary_guest,
        additional_guests: formData.additional_guests.filter(ag => ag.first_name && ag.first_name.trim()),
        // Include room_id for room assignment at check-in
        room_id: formData.room_id || null
      };

      await api.patch(`/bookings/${bookingId}/checkin`, checkinData);
      toast.success('Check-in successful!');
      onCheckInComplete?.();
      onOpenChange(false);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Check-in failed'));
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-4xl bg-white">
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-10 w-10 animate-spin text-emerald-500" />
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // Get the room number to display
  const getRoomDisplay = () => {
    if (details?.booking?.room_id) {
      return details?.room?.room_number || 'Unknown';
    }
    if (formData.room_id) {
      const selectedRoom = availableRooms.find(r => r.id === formData.room_id);
      return selectedRoom?.room_number || 'Selected';
    }
    return 'Not Assigned';
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl bg-white max-h-[90vh] overflow-y-auto" data-testid="checkin-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-gray-800">
            <LogIn className="h-5 w-5 text-emerald-600" />
            Check-In Guest - Room {getRoomDisplay()}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Room Selection (if not pre-assigned) */}
          {!details?.booking?.room_id && (
            <div className="p-4 bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl border border-amber-200">
              <div className="flex items-center gap-3 mb-3">
                <div className="h-12 w-12 rounded-xl bg-amber-100 flex items-center justify-center">
                  <Bed className="h-6 w-6 text-amber-600" />
                </div>
                <div>
                  <h3 className="font-bold text-lg text-gray-800">Select Room</h3>
                  <p className="text-sm text-gray-500">
                    {details?.booking?.room_type_name || 'Room'} - 
                    {details?.booking?.smoking_preference === 'smoking' ? ' Smoking' : 
                     details?.booking?.smoking_preference === 'non_smoking' ? ' Non-Smoking' : ' Any'}
                    {details?.booking?.bed_type_name ? ` - ${details?.booking?.bed_type_name}` : ''}
                  </p>
                </div>
              </div>
              
              {loadingRooms ? (
                <div className="flex items-center gap-2 text-amber-600">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Loading available rooms...
                </div>
              ) : availableRooms.length > 0 ? (
                <div className="space-y-3">
                  <Label className="text-gray-700">Available Rooms *</Label>
                  <Select 
                    value={formData.room_id} 
                    onValueChange={(value) => setFormData(prev => ({ ...prev, room_id: value }))}
                  >
                    <SelectTrigger className="border-amber-200" data-testid="room-select-checkin">
                      <SelectValue placeholder="Select a room to assign" />
                    </SelectTrigger>
                    <SelectContent>
                      {availableRooms.map(room => (
                        <SelectItem key={room.id} value={room.id}>
                          Room {room.room_number} - Floor {room.floor || 1}
                          {room.smoking_allowed ? ' (Smoking)' : ''}
                          {room.bed_type_name ? ` - ${room.bed_type_name}` : ''}
                          {room.price_per_night ? ` - ₹${room.price_per_night}/night` : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <div className="flex items-start gap-2 p-2 bg-amber-100 rounded-lg">
                    <Info className="h-4 w-4 text-amber-600 mt-0.5" />
                    <p className="text-xs text-amber-700">
                      {availableRooms.length} room(s) matching booking preferences. Select one to assign to this booking.
                    </p>
                  </div>
                </div>
              ) : (
                <div className="p-3 bg-red-50 rounded-lg border border-red-200">
                  <p className="text-red-600 text-sm">
                    No rooms available matching the booking preferences. Please check room inventory.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Room & Booking Info */}
          <div className="p-4 bg-gradient-to-r from-emerald-50 to-teal-50 rounded-xl border border-emerald-200">
            <div className="flex items-center gap-3 mb-3">
              <div className="h-12 w-12 rounded-xl bg-emerald-100 flex items-center justify-center">
                <Hotel className="h-6 w-6 text-emerald-600" />
              </div>
              <div>
                <h3 className="font-bold text-lg text-gray-800">
                  {details?.booking?.room_id ? `Room ${details?.room?.room_number}` : 
                   (formData.room_id ? `Room ${availableRooms.find(r => r.id === formData.room_id)?.room_number || 'Selected'}` : 
                   (details?.booking?.room_type_name || 'Room Type Booking'))}
                </h3>
                <p className="text-sm text-gray-500">
                  {details?.room?.room_type || details?.booking?.room_type_name || 'Standard'}
                </p>
              </div>
              <Badge className="ml-auto bg-emerald-100 text-emerald-700 border-emerald-200">
                Capacity: {details?.room_capacity || details?.room?.capacity || 2} guests
              </Badge>
            </div>
            
            {/* Editable Check-in/Check-out Dates */}
            <div className="grid grid-cols-2 gap-4 mt-4">
              <div className="space-y-2">
                <Label className="text-gray-700 flex items-center gap-1">
                  <Calendar className="h-3 w-3 text-emerald-600" />
                  Check-in Date *
                </Label>
                <Input
                  type="date"
                  value={formData.check_in ? formData.check_in.slice(0, 10) : ''}
                  onChange={(e) => setFormData({...formData, check_in: e.target.value})}
                  min={new Date().toISOString().slice(0, 10)}
                  className="border-emerald-200 focus:border-emerald-400"
                  data-testid="checkin-date-input"
                />
                <p className="text-xs text-gray-500">Cannot be in the past</p>
              </div>
              <div className="space-y-2">
                <Label className="text-gray-700 flex items-center gap-1">
                  <Calendar className="h-3 w-3 text-teal-600" />
                  Check-out Date *
                </Label>
                <Input
                  type="date"
                  value={formData.check_out ? formData.check_out.slice(0, 10) : ''}
                  onChange={(e) => setFormData({...formData, check_out: e.target.value})}
                  min={formData.check_in ? formData.check_in.slice(0, 10) : new Date().toISOString().slice(0, 10)}
                  className="border-teal-200 focus:border-teal-400"
                  data-testid="checkout-date-input"
                />
                <p className="text-xs text-gray-500">Must be after check-in</p>
              </div>
            </div>
            
            <div className="grid grid-cols-3 gap-4 mt-4 pt-4 border-t border-emerald-200">
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wider">Nights</p>
                <p className="font-semibold text-gray-800">{details?.booking?.total_nights || 1}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wider">Rate/Night</p>
                <p className="font-semibold text-gray-800">₹{details?.booking?.room_rate || 0}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wider">Total</p>
                <p className="font-bold text-emerald-600" style={{ fontFamily: 'JetBrains Mono' }}>
                  ₹{details?.booking?.total_amount?.toFixed(0) || 0}
                </p>
              </div>
            </div>
          </div>

          {/* Primary Guest Section */}
          <div className="p-4 bg-blue-50 rounded-xl border border-blue-200">
            <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <User className="h-4 w-4 text-blue-600" />
              Primary Guest Details
              {formData.existing_guest_id && (
                <Badge className="bg-green-100 text-green-700 border-green-300 text-xs">Existing Guest</Badge>
              )}
            </h3>
            
            {/* Mobile Number - First Field */}
            <div className="grid grid-cols-3 gap-4 mb-4">
              <div className="space-y-2">
                <Label className="text-gray-700 flex items-center gap-1">
                  <Phone className="h-3 w-3" />
                  Mobile Number *
                </Label>
                <div className="relative">
                  <PhoneInput
                    value={formData.primary_guest.phone}
                    onChange={(value) => {
                      updatePrimaryGuest('phone', value);
                      searchGuestByPhone(value);
                    }}
                    placeholder="98765 43210"
                    required
                    data-testid="checkin-primary-phone"
                  />
                  {searchingPhone && (
                    <Loader2 className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-blue-500" />
                  )}
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-gray-700">First Name *</Label>
                <Input
                  value={formData.primary_guest.first_name}
                  onChange={(e) => updatePrimaryGuest('first_name', e.target.value)}
                  placeholder="First name"
                  required
                  className="border-blue-200 focus:border-blue-400"
                  data-testid="checkin-primary-first-name"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-700">Last Name</Label>
                <Input
                  value={formData.primary_guest.last_name}
                  onChange={(e) => updatePrimaryGuest('last_name', e.target.value)}
                  placeholder="Last name"
                  className="border-blue-200 focus:border-blue-400"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-3 gap-4 mb-4">
              <div className="space-y-2">
                <Label className="text-gray-700">Email</Label>
                <Input
                  type="email"
                  value={formData.primary_guest.email}
                  onChange={(e) => updatePrimaryGuest('email', e.target.value)}
                  placeholder="email@example.com"
                  className="border-blue-200 focus:border-blue-400"
                />
              </div>
              <div className="col-span-2 space-y-2">
                <Label className="text-gray-700">
                  Address * <span className="text-xs text-red-500">(Mandatory)</span>
                </Label>
                <AddressInput
                  value={formData.primary_guest.address}
                  onChange={(value) => updatePrimaryGuest('address', value)}
                  required
                  className="border-red-200"
                />
              </div>
            </div>
            
            {/* ID Document - MANDATORY at Check-in */}
            <div className="grid grid-cols-2 gap-4 p-3 bg-red-50 rounded-lg border border-red-200">
              <div className="space-y-2">
                <Label className="text-gray-700 flex items-center gap-1">
                  <CreditCard className="h-3 w-3 text-red-600" />
                  ID Document Type *
                  <span className="text-xs text-red-500">(Mandatory)</span>
                </Label>
                <Select 
                  value={formData.primary_guest.id_type} 
                  onValueChange={(v) => updatePrimaryGuest('id_type', v)}
                  required
                >
                  <SelectTrigger className="border-red-200" data-testid="checkin-primary-id-type">
                    <SelectValue placeholder="Select ID Type" />
                  </SelectTrigger>
                  <SelectContent>
                    {idTypes.map(type => (
                      <SelectItem key={type.id} value={type.name}>{type.name}</SelectItem>
                    ))}
                    <SelectItem value="Aadhaar">Aadhaar Card</SelectItem>
                    <SelectItem value="Passport">Passport</SelectItem>
                    <SelectItem value="Driving License">Driving License</SelectItem>
                    <SelectItem value="Voter ID">Voter ID</SelectItem>
                    <SelectItem value="PAN Card">PAN Card</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-gray-700">ID Number * <span className="text-xs text-red-500">(Mandatory)</span></Label>
                <Input
                  value={formData.primary_guest.id_number}
                  onChange={(e) => updatePrimaryGuest('id_number', e.target.value)}
                  placeholder="Enter ID number"
                  required
                  className="border-red-200 focus:border-red-400"
                  data-testid="checkin-primary-id-number"
                />
              </div>
            </div>
            
            {/* ID Document Photo Upload */}
            <div className="mt-3">
              <IdDocumentUpload
                value={formData.primary_guest.id_proof_image}
                onChange={(url) => updatePrimaryGuest('id_proof_image', url)}
                label="ID Document Photo (Recommended)"
                guestId={formData.existing_guest_id}
              />
            </div>
          </div>

          {/* Additional Guests Section */}
          <div className="p-4 bg-purple-50 rounded-xl border border-purple-200">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-gray-800 flex items-center gap-2">
                <Users className="h-4 w-4 text-purple-600" />
                Additional Guests
                <span className="text-xs text-gray-500 font-normal">
                  ({formData.additional_guests.length}/{(details?.room_capacity || 2) - 1} max)
                </span>
              </h3>
              {formData.additional_guests.length < (details?.room_capacity || 2) - 1 && (
                <Button 
                  type="button" 
                  variant="outline" 
                  size="sm" 
                  onClick={addAdditionalGuest}
                  className="border-purple-300 text-purple-600 hover:bg-purple-100"
                  data-testid="add-additional-guest-btn"
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Add Guest
                </Button>
              )}
            </div>
            
            {/* Capacity Warning */}
            {!canCheckIn() && (
              <div className="p-3 mb-4 bg-red-100 border border-red-300 rounded-lg flex items-center gap-3">
                <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0" />
                <div>
                  <p className="font-medium text-red-700">Guest Limit Exceeded</p>
                  <p className="text-sm text-red-600">
                    {getTotalGuests()} guests selected, but room capacity is {details?.room_capacity}.
                  </p>
                </div>
              </div>
            )}
            
            {formData.additional_guests.length > 0 ? (
              <div className="border border-purple-200 rounded-lg overflow-hidden">
                <div className="px-3 py-2 bg-purple-50 text-xs text-purple-700">
                  * First Name, Last Name, ID Type, ID Number required. Phone & Address optional.
                </div>
                <Table>
                  <TableHeader>
                    <TableRow className="bg-purple-100">
                      <TableHead className="text-gray-700">First Name *</TableHead>
                      <TableHead className="text-gray-700">Last Name *</TableHead>
                      <TableHead className="text-gray-700">Mobile</TableHead>
                      <TableHead className="text-gray-700">ID Type *</TableHead>
                      <TableHead className="text-gray-700">ID Number *</TableHead>
                      <TableHead className="text-gray-700">ID Photo</TableHead>
                      <TableHead className="w-10"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {formData.additional_guests.map((guest, index) => (
                      <TableRow key={index} className="bg-white">
                        <TableCell>
                          <Input
                            value={guest.first_name}
                            onChange={(e) => updateAdditionalGuest(index, 'first_name', e.target.value)}
                            placeholder="First name *"
                            className="h-8 text-sm border-purple-200"
                            required
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            value={guest.last_name}
                            onChange={(e) => updateAdditionalGuest(index, 'last_name', e.target.value)}
                            placeholder="Last name *"
                            className="h-8 text-sm border-purple-200"
                            required
                          />
                        </TableCell>
                        <TableCell>
                          <PhoneInput
                            value={guest.phone}
                            onChange={(value) => updateAdditionalGuest(index, 'phone', value)}
                            placeholder="Mobile (Optional)"
                            className="h-8 text-sm"
                          />
                        </TableCell>
                        <TableCell>
                          <Select 
                            value={guest.id_type} 
                            onValueChange={(v) => updateAdditionalGuest(index, 'id_type', v)}
                          >
                            <SelectTrigger className="h-8 text-sm border-purple-200">
                              <SelectValue placeholder="ID Type *" />
                            </SelectTrigger>
                            <SelectContent>
                              {idTypes.map(type => (
                                <SelectItem key={type.id} value={type.name}>{type.name}</SelectItem>
                              ))}
                              <SelectItem value="Aadhaar">Aadhaar</SelectItem>
                              <SelectItem value="Passport">Passport</SelectItem>
                              <SelectItem value="DL">DL</SelectItem>
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell>
                          <Input
                            value={guest.id_number}
                            onChange={(e) => updateAdditionalGuest(index, 'id_number', e.target.value)}
                            placeholder="ID Number *"
                            className="h-8 text-sm border-purple-200"
                            required
                          />
                        </TableCell>
                        <TableCell>
                          {guest.id_proof_image ? (
                            <div className="flex items-center gap-1">
                              <span className="text-xs text-green-600">✓</span>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => updateAdditionalGuest(index, 'id_proof_image', '')}
                                className="h-6 px-2 text-xs text-red-500"
                              >
                                Remove
                              </Button>
                            </div>
                          ) : (
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                const input = document.createElement('input');
                                input.type = 'file';
                                input.accept = 'image/*';
                                input.onchange = async (e) => {
                                  const file = e.target.files?.[0];
                                  if (file) {
                                    const fd = new FormData();
                                    fd.append('file', file);
                                    try {
                                      const res = await api.post('/upload/id-document', fd, {
                                        headers: { 'Content-Type': 'multipart/form-data' }
                                      });
                                      updateAdditionalGuest(index, 'id_proof_image', res.data.document_url);
                                      toast.success('ID uploaded');
                                    } catch {
                                      toast.error('Upload failed');
                                    }
                                  }
                                };
                                input.click();
                              }}
                              className="h-7 px-2 text-xs border-purple-200"
                            >
                              <Camera className="h-3 w-3 mr-1" />
                              Add *
                            </Button>
                          )}
                        </TableCell>
                        <TableCell>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={() => removeAdditionalGuest(index)}
                            className="h-8 w-8 text-red-500 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <p className="text-sm text-gray-500 text-center py-4">
                No additional guests. Click &quot;Add Guest&quot; to add more guests.
              </p>
            )}
            <p className="text-xs text-gray-500 mt-2">
              * ID documents for additional guests are optional
            </p>
          </div>

          {/* Advance Payment & Discount Section */}
          <div className="p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl border border-green-200">
            <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <CreditCard className="h-4 w-4 text-green-600" />
              Payment Details
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-700">Advance Payment (₹)</Label>
                <Input
                  type="number"
                  value={formData.advance_paid}
                  onChange={(e) => setFormData({...formData, advance_paid: e.target.value})}
                  placeholder="0"
                  className="border-green-200 focus:border-green-400"
                  data-testid="checkin-advance-input"
                />
                <p className="text-xs text-gray-500">Amount received in advance</p>
              </div>
              <div className="space-y-2">
                <Label className="text-gray-700">Discount (₹)</Label>
                <Input
                  type="number"
                  value={formData.discount}
                  onChange={(e) => setFormData({...formData, discount: e.target.value})}
                  placeholder="0"
                  className="border-green-200 focus:border-green-400"
                  data-testid="checkin-discount-input"
                />
                <p className="text-xs text-gray-500">Discount applied to booking</p>
              </div>
            </div>
            {/* Updated Summary */}
            {(formData.advance_paid || formData.discount) && (
              <div className="mt-3 p-2 bg-white rounded-lg border border-green-100">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Original Total:</span>
                  <span className="font-semibold">₹{details?.booking?.total_amount?.toFixed(0) || 0}</span>
                </div>
                {parseFloat(formData.discount) > 0 && (
                  <div className="flex justify-between text-sm text-red-600">
                    <span>Discount:</span>
                    <span>- ₹{parseFloat(formData.discount || 0).toFixed(0)}</span>
                  </div>
                )}
                <div className="flex justify-between text-sm font-semibold border-t pt-1 mt-1">
                  <span>Net Total:</span>
                  <span>₹{((details?.booking?.total_amount || 0) - parseFloat(formData.discount || 0)).toFixed(0)}</span>
                </div>
                {parseFloat(formData.advance_paid) > 0 && (
                  <div className="flex justify-between text-sm text-green-600">
                    <span>Advance Paid:</span>
                    <span>- ₹{parseFloat(formData.advance_paid || 0).toFixed(0)}</span>
                  </div>
                )}
                <div className="flex justify-between text-sm font-bold text-emerald-700 border-t pt-1 mt-1">
                  <span>Balance Due:</span>
                  <span>₹{((details?.booking?.total_amount || 0) - parseFloat(formData.discount || 0) - parseFloat(formData.advance_paid || 0)).toFixed(0)}</span>
                </div>
              </div>
            )}
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label className="text-gray-700">Check-in Notes</Label>
            <Input
              value={formData.notes}
              onChange={(e) => setFormData({...formData, notes: e.target.value})}
              placeholder="Any special requests or notes..."
              className="border-gray-200"
              data-testid="checkin-notes"
            />
          </div>
        </div>

        <DialogFooter className="mt-6">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button 
            onClick={handleCheckIn}
            disabled={processing || !canCheckIn()}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="confirm-checkin-btn"
          >
            {processing ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Processing...
              </>
            ) : (
              <>
                <LogIn className="h-4 w-4 mr-2" />
                Complete Check-In
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
